import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Handshake,
  Search,
  Save,
  AlertTriangle,
  Clock,
  TrendingUp,
  Building2,
  ChevronRight,
  Power,
} from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

function Progress({ value, color = "#7c3aed" }) {
  const pct = Math.min(100, Math.max(0, value || 0));
  return (
    <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function SchoolRow({ s, onSave, onToggle }) {
  const [form, setForm] = useState({
    fdp_max_value: s.fdp_max_value || 0,
    fdp_period_months: s.fdp_period_months || 3,
    fdp_start_date: s.fdp_start_date || new Date().toISOString().slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const state = s.state;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(s.id, {
        fdp_max_value: Number(form.fdp_max_value) || 0,
        fdp_period_months: Number(form.fdp_period_months) || 0,
        fdp_start_date: form.fdp_start_date,
      });
      toast.success("Configuração salva");
    } catch (e) {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 md:p-5" data-testid={`fdp-row-${s.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-[#1e3a8a]/10 flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-[#1e3a8a]" />
          </div>
          <div className="min-w-0">
            <p className="font-heading font-bold text-slate-900 truncate">{s.name}</p>
            <p className="text-xs text-slate-500 truncate">{s.city || "—"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={!!s.fdp_active}
            onCheckedChange={(v) => onToggle(s.id, v)}
            data-testid={`fdp-toggle-${s.id}`}
          />
          <span className={`text-xs font-semibold ${s.fdp_active ? "text-green-600" : "text-slate-500"}`}>
            {s.fdp_active ? "Ativa no FDP" : "Inativa"}
          </span>
        </div>
      </div>

      {s.fdp_active && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Valor máximo (R$)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.fdp_max_value}
                onChange={(e) => setForm({ ...form, fdp_max_value: e.target.value })}
                data-testid={`fdp-max-${s.id}`}
                className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Período (meses)</label>
              <input
                type="number"
                min="1"
                max="60"
                value={form.fdp_period_months}
                onChange={(e) => setForm({ ...form, fdp_period_months: e.target.value })}
                data-testid={`fdp-period-${s.id}`}
                className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Início</label>
              <input
                type="date"
                value={form.fdp_start_date}
                onChange={(e) => setForm({ ...form, fdp_start_date: e.target.value })}
                data-testid={`fdp-start-${s.id}`}
                className="w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
              />
            </div>
          </div>

          {state && (
            <div className="mt-4 rounded-xl bg-slate-50 p-4 space-y-3">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                <div className="text-sm">
                  <span className="font-semibold text-slate-900">{BRL(state.consumed)}</span>
                  <span className="text-slate-500"> de {BRL(state.max_value)}</span>
                  <span className="text-slate-400 text-xs"> ({state.percent_value}%)</span>
                </div>
                <div className="text-xs text-slate-600 flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {state.days_left} dias restantes
                  </span>
                  {state.end_date && <span>até {formatDateBR(state.end_date)}</span>}
                </div>
              </div>
              <Progress
                value={state.percent_value}
                color={state.over_value ? "#ef4444" : state.near_value_limit ? "#f97316" : "#7c3aed"}
              />
              <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
                {state.has_active_budget && (
                  <Link
                    to={`/orcamentos/${state.active_budget_id}`}
                    className="inline-flex items-center gap-1 bg-[#1e3a8a]/10 text-[#1e3a8a] px-2 py-0.5 rounded-full hover:bg-[#1e3a8a]/20"
                  >
                    Orç. #{state.active_budget_number}
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                )}
                {!state.has_active_budget && (
                  <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">Sem orçamento FDP aberto</span>
                )}
                {state.near_value_limit && !state.over_value && (
                  <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Próximo do limite
                  </span>
                )}
                {state.over_value && (
                  <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Limite atingido
                  </span>
                )}
                {state.near_time_limit && !state.over_time && (
                  <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Período acabando
                  </span>
                )}
                {state.over_time && (
                  <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Período vencido
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end mt-3">
            <Button
              onClick={save}
              disabled={saving}
              size="sm"
              className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
              data-testid={`fdp-save-${s.id}`}
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function FazDepoisPaga() {
  const [schools, setSchools] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [s, sum] = await Promise.all([
      api.get("/fdp/schools"),
      api.get("/fdp/summary"),
    ]);
    setSchools(s.data);
    setSummary(sum.data);
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async (id, payload) => {
    await api.patch(`/fdp/schools/${id}`, payload);
    await load();
  };

  const handleToggle = async (id, v) => {
    await api.patch(`/fdp/schools/${id}`, { fdp_active: v });
    toast.success(v ? "Escola ativada no FDP" : "Escola desativada do FDP");
    await load();
  };

  const alertSchoolIds = useMemo(
    () => new Set((summary?.alerts || []).map((a) => a.school_id)),
    [summary],
  );

  const filtered = schools.filter((s) => {
    if (filter === "active" && !s.fdp_active) return false;
    if (filter === "inactive" && s.fdp_active) return false;
    if (filter === "alert" && !alertSchoolIds.has(s.id)) return false;
    if (q) {
      const t = q.toLowerCase();
      if (!s.name.toLowerCase().includes(t) && !(s.city || "").toLowerCase().includes(t)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Handshake}
        title="Faz Depois Paga"
        subtitle="Configure limite e período por escola. Cada escola tem um orçamento FDP por vez."
      />

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4" data-testid="fdp-summary-schools">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#1e3a8a]" />
              </div>
              <div>
                <p className="font-heading text-2xl font-black text-slate-900 leading-none">{summary.active_schools}</p>
                <p className="text-xs text-slate-600 mt-1">Escolas ativas no FDP</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4" data-testid="fdp-summary-consumed">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#7c3aed]/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#7c3aed]" />
              </div>
              <div>
                <p className="font-heading text-lg font-black text-slate-900 leading-none">{BRL(summary.total_consumed)}</p>
                <p className="text-xs text-slate-600 mt-1">
                  Consumido de {BRL(summary.total_max)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4" data-testid="fdp-summary-alerts">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="font-heading text-2xl font-black text-slate-900 leading-none">{summary.alerts.length}</p>
                <p className="text-xs text-slate-600 mt-1">Alertas (limite/vencimento)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Pesquisar escola..."
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed]"
            data-testid="fdp-search"
          />
        </div>
        <div className="flex gap-2">
          {[
            { v: "all",      l: "Todas" },
            { v: "active",   l: "Ativas" },
            { v: "inactive", l: "Inativas" },
            { v: "alert",    l: `Com alerta${(summary?.alerts?.length || 0) ? ` (${summary.alerts.length})` : ""}` },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setFilter(t.v)}
              data-testid={`fdp-filter-${t.v}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold ${
                filter === t.v ? "bg-[#1e3a8a] text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-12 text-center">
          <Handshake className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Nenhuma escola encontrada.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <SchoolRow key={s.id} s={s} onSave={handleSave} onToggle={handleToggle} />
          ))}
        </div>
      )}
    </div>
  );
}
